import { lazy, Suspense } from 'react'
import { ExternalLink, ChevronDown, ChevronUp, Zap, MapPin, XCircle } from 'lucide-react'
import type { ChargepointSummaryWithDistance, SpeedTier } from '@fuelfinder/shared'
import { operatorLabel } from '../lib/format'

const MiniMap = lazy(() => import('./MiniMap').then((m) => ({ default: m.MiniMap })))

interface ChargepointCardProps {
  chargepoint: ChargepointSummaryWithDistance
  expanded?: boolean
  onToggle?: () => void
}

/**
 * Get Tailwind text color class based on speed tier
 */
function getTierColorClass(tier: SpeedTier): string {
  if (tier === 'rapid') return 'text-brand-500'
  if (tier === 'fast') return 'text-brand-400'
  return 'text-amber-500'
}

/**
 * Get Tailwind border color class based on speed tier
 */
function getTierBorderClass(tier: SpeedTier): string {
  if (tier === 'rapid') return 'border-brand-500'
  if (tier === 'fast') return 'border-brand-400'
  return 'border-amber-400'
}

/**
 * Format power in kW
 */
function formatPower(kw: number): string {
  if (kw === 0) return 'N/A'
  return `${Math.round(kw)} kW`
}

/**
 * Format distance in miles
 */
function formatDistance(miles: number): string {
  if (miles < 0.1) return '< 0.1 mi'
  return `${miles.toFixed(1)} mi`
}

/**
 * Get Google Maps directions URL
 */
function getDirectionsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
}

/**
 * Render lightning bolt icons for speed tier
 */
function BoltIcons({ tier }: { tier: SpeedTier }) {
  const count = tier === 'rapid' ? 3 : tier === 'fast' ? 2 : 1
  return (
    <span className="inline-flex">
      {Array.from({ length: count }, (_, i) => (
        <Zap key={i} size={14} className="fill-current -ml-0.5 first:ml-0" />
      ))}
    </span>
  )
}

/**
 * Loading placeholder for mini map
 */
function MiniMapLoading() {
  return (
    <div className="w-full h-40 bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
      <div className="flex flex-col items-center gap-2 text-slate-400 dark:text-slate-500">
        <MapPin size={24} className="animate-pulse" />
        <span className="text-sm">Loading map...</span>
      </div>
    </div>
  )
}

export function StationCard({
  chargepoint,
  expanded = false,
  onToggle,
}: ChargepointCardProps) {
  const tierColorClass = getTierColorClass(chargepoint.speedTier)
  const tierBorderClass = getTierBorderClass(chargepoint.speedTier)

  return (
    <div
      className={`bg-white dark:bg-slate-800 rounded-lg shadow-sm border-2 ${tierBorderClass} overflow-hidden`}
      onClick={onToggle}
      role={onToggle ? 'button' : undefined}
      tabIndex={onToggle ? 0 : undefined}
      onKeyDown={
        onToggle
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onToggle()
              }
            }
          : undefined
      }
    >
      {/* Main card content */}
      <div className="p-4 cursor-pointer">
        <div className="flex items-start justify-between gap-3">
          {/* Left: Operator, name, distance */}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white truncate">
              {operatorLabel(chargepoint.operator)}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
              {chargepoint.name}
            </p>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-sm text-slate-600 dark:text-slate-300">
                {formatDistance(chargepoint.distanceMiles)}
              </span>
              <span className="text-xs text-slate-400 dark:text-slate-500">
                {chargepoint.totalConnectors} connector{chargepoint.totalConnectors !== 1 ? 's' : ''}
              </span>
              {!chargepoint.isOperational && (
                <span className="flex items-center gap-0.5 text-xs text-red-500">
                  <XCircle size={12} />
                  Offline
                </span>
              )}
            </div>
          </div>

          {/* Right: Power and expand indicator */}
          <div className="flex flex-col items-end">
            <span className={`text-2xl font-bold ${tierColorClass} flex items-center gap-1`}>
              <BoltIcons tier={chargepoint.speedTier} />
              {formatPower(chargepoint.maxPowerKW)}
            </span>
            {onToggle && (
              <span className="text-slate-400 mt-2">
                {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
          <Suspense fallback={<MiniMapLoading />}>
            <MiniMap lat={chargepoint.lat} lng={chargepoint.lng} brand={chargepoint.operator} />
          </Suspense>

          <div className="px-4 py-4">
            {/* Connectors summary */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-2">
                Connectors ({chargepoint.totalConnectors})
              </h4>
              <p className="text-sm text-slate-700 dark:text-slate-200">
                {chargepoint.connectorSummary}
              </p>
            </div>

            {/* Usage info */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-2">
                Access
              </h4>
              <p className="text-sm text-slate-700 dark:text-slate-200">{chargepoint.usageType}</p>
            </div>

            {/* Location */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-2">
                Location
              </h4>
              <p className="text-base text-slate-700 dark:text-slate-200">
                {chargepoint.town} {chargepoint.postcode}
              </p>
            </div>

            {/* Navigate button */}
            <a
              href={getDirectionsUrl(chargepoint.lat, chargepoint.lng)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center justify-center gap-2 w-full py-4 bg-brand-500 hover:bg-brand-600 text-white text-lg font-semibold rounded-lg transition-colors"
            >
              <ExternalLink size={20} />
              Navigate
            </a>
          </div>
        </div>
      )}
    </div>
  )
}
